import { Badge, Col, Menu, Row, Space } from "antd";
import type { MenuProps } from "antd";
import {
    BarsOutlined,
    BranchesOutlined,
    DatabaseOutlined,
    ExclamationCircleOutlined,
    GlobalOutlined,
    RiseOutlined,
    SettingOutlined,
    WarningOutlined
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate, useOutletContext } from "react-router";
import { IResourceDetails } from "../../types";
import { CONFIG_CATEGORIES } from "../../enums/enums";
import MenuItemNotification from "../../components/MenuItemNotification";

export default function ResourceSettingsRoot() {
    const navigate = useNavigate();
    const location = useLocation();
    const { resourceDetails } = useOutletContext<{ resourceDetails: IResourceDetails }>();
    const [activeTab, setActiveTab] = useState("general");

    useEffect(() => {
        if (!location.pathname.split("/")[8]) {
            const items = getMenuItems();
            navigate(`${location.pathname}/${items[0]?.key}`);
        } else {
            setActiveTab(location.pathname.split("/")[8]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const getMenuItems = () => {
        let items: MenuProps["items"] = [];
        const resourceProps = resourceDetails.component.schema.properties.inputs.properties;

        if (Object.values(
            resourceProps
        ).find(v => !CONFIG_CATEGORIES.includes(v.category!))
        ) {
            items.push({
                key: "general",
                label: <MenuItemNotification
                    icon={<BarsOutlined />}
                    text="General"
                />
            });
        }

        if (Object.values(
            resourceProps
        ).find(v => ["scaling", "autoscaling", "allocation"].includes(v.category!))
        ) {
            items.push({
                key: "scaling",
                label: <MenuItemNotification
                    icon={<RiseOutlined />}
                    text="Scaling"
                    badgeText="New"
                />
            });
        }

        if (Object.values(
            resourceProps
        ).find(v => ["alarm"].includes(v.category!))
        ) {
            items.push({
                key: "alarms",
                label: <MenuItemNotification
                    icon={<WarningOutlined />}
                    text="Alarms"
                    badgeText="New"
                />
            });
        }

        if (Object.values(
            resourceProps
        ).find(v => ["storage"].includes(v.category!))
        ) {
            items.push({
                key: "storage",
                label: <MenuItemNotification
                    icon={<DatabaseOutlined />}
                    text="Storage"
                />
            });
        }

        if (resourceDetails.domain.eligibleForDomainConfiguration) {
            items.push({
                key: "domain",
                label: <MenuItemNotification
                    icon={<GlobalOutlined />}
                    text="Domain"
                />
            })
        }

        if (!resourceDetails.isPublic) {
            items.push({
                key: "pipeline",
                label: <MenuItemNotification
                    icon={<BranchesOutlined />}
                    text="Pipeline"
                />
            })
        }

        if (Object.values(
            resourceProps
        ).find(v => ["variables"].includes(v.category!))
        ) {
            items.push({
                key: "variables",
                label: <MenuItemNotification
                    icon={<BarsOutlined />}
                    text="Variables"
                    badgeText="New"
                />
            });
        }

        if (Object.values(
            resourceProps
        ).find(v => ["advanced"].includes(v.category!))
        ) {
            items.push({
                key: "advanced",
                label: <MenuItemNotification
                    icon={<SettingOutlined />}
                    text="Advanced"
                />
            });
        }

        items.push({
            key: "danger-zone",
            label: <MenuItemNotification
                icon={<ExclamationCircleOutlined />}
                text="Danger Zone"
            />,
            danger: true
        })

        return items;
    }

    return (
        <Row gutter={24}>
            <Col span={6}>
                <Menu
                    mode="inline"
                    className="ant-card-bordered"
                    selectedKeys={[activeTab]}
                    onSelect={(item) => {
                        navigate(item.key);
                        setActiveTab(item.key);
                    }}
                    items={getMenuItems()}
                />
            </Col>
            <Col span={18}>
                <Outlet context={{ resourceDetails }} />
            </Col>
        </Row >
    )
}